import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { localDateKey, msUntilNextLocalMidnight } from './date';

export const STORAGE_NAMESPACE = 'my-hub';

/**
 * The registry of storage areas. Adding one is a Mayor change.
 * This union is what stops two workers both picking `my-hub:settings` —
 * a collision becomes a TypeScript error instead of a data-loss bug.
 *
 *   ui       Mayor        theme
 *   top      Mayor        One Priority (daily)
 *   btc      btc          wallet stack + caches (NOT daily)
 *   weather  weather-cal  cached observations
 *   calendar weather-cal  OAuth token
 *   music    music-quote  last query / last track
 *   quote    music-quote  quote-of-the-day cache (daily)
 *   health   dailies      field definitions (persistent) + today's checks (daily)
 *   todos    dailies      today's list (daily)
 *   media    dailies      tracked items (NOT daily)
 */
export type StorageArea =
  | 'ui'
  | 'top'
  | 'btc'
  | 'weather'
  | 'calendar'
  | 'music'
  | 'quote'
  | 'health'
  | 'todos'
  | 'media';

export function storageKey(area: StorageArea, key: string): string {
  return `${STORAGE_NAMESPACE}:${area}:${key}`;
}

/* ------------------------------------------------------------------ */
/* Backing store: localStorage if usable, an in-memory Map if not.     */
/* ------------------------------------------------------------------ */

const memory = new Map<string, string>();
let backing: Storage | null | undefined; // undefined = not yet probed

function store(): Storage | null {
  if (backing !== undefined) return backing;
  try {
    const probe = `${STORAGE_NAMESPACE}:__probe__`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    backing = window.localStorage;
  } catch {
    // Private mode / blocked site data: fall back to a session-scoped store.
    backing = null;
  }
  return backing;
}

function rawGet(k: string): string | null {
  const s = store();
  if (s === null) return memory.get(k) ?? null;
  try {
    return s.getItem(k);
  } catch {
    return memory.get(k) ?? null;
  }
}

function rawSet(k: string, v: string): void {
  memory.set(k, v); // always mirror, so a quota failure still works this session
  const s = store();
  if (s === null) return;
  try {
    s.setItem(k, v);
  } catch {
    /* QuotaExceededError - the memory mirror is the fallback. Never throw. */
  }
}

function rawRemove(k: string): void {
  memory.delete(k);
  const s = store();
  if (s === null) return;
  try {
    s.removeItem(k);
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------------------------ */
/* Change notification: same-page (two hooks on one key) + cross-tab.  */
/* ------------------------------------------------------------------ */

const keyListeners = new Map<string, Set<() => void>>();

function notify(fullKey: string): void {
  keyListeners.get(fullKey)?.forEach((fn) => fn());
}

function subscribeKey(fullKey: string, fn: () => void): () => void {
  let set = keyListeners.get(fullKey);
  if (set === undefined) {
    set = new Set();
    keyListeners.set(fullKey, set);
  }
  set.add(fn);
  return () => {
    const current = keyListeners.get(fullKey);
    current?.delete(fn);
    if (current !== undefined && current.size === 0) keyListeners.delete(fullKey);
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key.startsWith(`${STORAGE_NAMESPACE}:`)) notify(e.key);
  });
}

/* ------------------------------------------------------------------ */
/* Read / write                                                        */
/* ------------------------------------------------------------------ */

export type Validator<T> = (value: unknown) => value is T;

/**
 * Never throws. Corrupt JSON, or a value failing `isValid`, is DELETED and the
 * fallback returned — a bad blob must not white-screen the app and must not
 * fail again on the next render.
 */
export function readJson<T>(
  area: StorageArea,
  key: string,
  fallback: T,
  isValid?: Validator<T>,
): T {
  const k = storageKey(area, key);
  const text = rawGet(k);
  if (text === null) return fallback;

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    rawRemove(k);
    return fallback;
  }
  if (isValid !== undefined && !isValid(parsed)) {
    rawRemove(k);
    return fallback;
  }
  return parsed as T;
}

/** Never throws. A non-serialisable value is dropped rather than propagated. */
export function writeJson<T>(area: StorageArea, key: string, value: T): void {
  const k = storageKey(area, key);
  let text: string;
  try {
    text = JSON.stringify(value);
  } catch {
    return; // circular ref / BigInt
  }
  rawSet(k, text);
  notify(k);
}

export function removeJson(area: StorageArea, key: string): void {
  const k = storageKey(area, key);
  rawRemove(k);
  notify(k);
}

/* ------------------------------------------------------------------ */
/* The shared local-calendar-date clock (one timer for the whole app)  */
/* ------------------------------------------------------------------ */

const dateListeners = new Set<() => void>();
let currentDateKey = localDateKey();
let rolloverTimer: number | undefined;
let safetyTimer: number | undefined;

function tick(): void {
  const next = localDateKey();
  if (next !== currentDateKey) {
    currentDateKey = next;
    dateListeners.forEach((fn) => fn());
  }
  armRollover();
}

function armRollover(): void {
  if (rolloverTimer !== undefined) window.clearTimeout(rolloverTimer);
  // +1s slop: without it a timer firing a millisecond early re-arms with a
  // ~0ms delay and spins until the wall clock catches up.
  rolloverTimer = window.setTimeout(tick, msUntilNextLocalMidnight() + 1_000);
}

function startClock(): void {
  if (dateListeners.size !== 1) return; // only on the first subscriber
  armRollover();
  // A pinned tab is exactly where one setTimeout is unreliable: background
  // tabs throttle timers and a sleeping laptop skips them entirely.
  safetyTimer = window.setInterval(tick, 60_000);
  window.addEventListener('visibilitychange', tick);
  window.addEventListener('focus', tick);
}

function stopClock(): void {
  if (dateListeners.size !== 0) return;
  if (rolloverTimer !== undefined) window.clearTimeout(rolloverTimer);
  if (safetyTimer !== undefined) window.clearInterval(safetyTimer);
  rolloverTimer = undefined;
  safetyTimer = undefined;
  window.removeEventListener('visibilitychange', tick);
  window.removeEventListener('focus', tick);
}

const subscribeDate = (onChange: () => void): (() => void) => {
  dateListeners.add(onChange);
  startClock();
  return () => {
    dateListeners.delete(onChange);
    stopClock();
  };
};

const getDateSnapshot = (): string => currentDateKey;

/** 'YYYY-MM-DD' in local time. Re-renders the caller when the day rolls over. */
export function useLocalDateKey(): string {
  return useSyncExternalStore(subscribeDate, getDateSnapshot, getDateSnapshot);
}

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

export type Setter<T> = (next: T | ((prev: T) => T)) => void;

/** Persists forever (media items, BTC wallets, theme). Does NOT reset daily. */
export function usePersistentState<T>(
  area: StorageArea,
  key: string,
  initial: T,
  isValid?: Validator<T>,
): readonly [T, Setter<T>] {
  const [value, setValue] = useState<T>(() => readJson(area, key, initial, isValid));
  const ref = useRef(value);
  ref.current = value;

  const set = useCallback<Setter<T>>(
    (next) => {
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(ref.current) : next;
      ref.current = resolved;
      writeJson(area, key, resolved);
      setValue(resolved);
    },
    [area, key],
  );

  // Another tab, or another hook on the same key, changed it.
  useEffect(
    () =>
      subscribeKey(storageKey(area, key), () => {
        const fresh = readJson(area, key, initial, isValid);
        ref.current = fresh;
        setValue(fresh);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [area, key],
  );

  return [value, set] as const;
}

interface DailyEnvelope<T> {
  readonly date: string; // local calendar date key, 'YYYY-MM-DD'
  readonly value: T;
}

function isEnvelope(v: unknown): v is DailyEnvelope<unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { date?: unknown }).date === 'string' &&
    'value' in v
  );
}

/**
 * Persists within the local calendar day and resets to `initial` when the day
 * changes — including a rollover while the tab stays open (the pinned-tab
 * case), driven by the shared clock above.
 *
 * Returns [value, set, todayKey]. `todayKey` is handed back so panels that
 * cache per-day remote data (Quote of the Day) key off the same string the
 * reset uses, rather than computing their own and disagreeing at midnight.
 *
 * Deliberately does NOT write on mount: a panel the user never touches leaves
 * no key behind, so "resets at midnight" is observable as absence.
 */
export function useDailyState<T>(
  area: StorageArea,
  key: string,
  initial: T,
  isValid?: Validator<T>,
): readonly [T, Setter<T>, string] {
  const today = useLocalDateKey();

  const readForDay = useCallback(
    (dayKey: string): T => {
      const stored = readJson<DailyEnvelope<unknown> | null>(
        area,
        key,
        null,
        (v): v is DailyEnvelope<unknown> | null => v === null || isEnvelope(v),
      );
      if (stored === null) return initial;
      if (stored.date !== dayKey) return initial; // yesterday -> reset
      if (isValid !== undefined && !isValid(stored.value)) return initial; // shape drift -> reset
      return stored.value as T;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [area, key],
  );

  const [value, setValue] = useState<T>(() => readForDay(today));
  const ref = useRef(value);
  ref.current = value;
  const dayRef = useRef(today);

  // Midnight rollover while mounted.
  useEffect(() => {
    if (dayRef.current === today) return;
    dayRef.current = today;
    const fresh = readForDay(today);
    ref.current = fresh;
    setValue(fresh);
    writeJson<DailyEnvelope<T>>(area, key, { date: today, value: fresh });
  }, [today, readForDay, area, key]);

  const set = useCallback<Setter<T>>(
    (next) => {
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(ref.current) : next;
      ref.current = resolved;
      writeJson<DailyEnvelope<T>>(area, key, { date: dayRef.current, value: resolved });
      setValue(resolved);
    },
    [area, key],
  );

  return [value, set, today] as const;
}
